import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface ConfidenceSignals {
  cited_reference_count?: number;
  llm_hedged?: boolean;
  had_unprocessed_images?: boolean;
  external_system_hint?: string;
  reason?: string;
  actionable_ontology_count?: number;
  // Set by the trivial-reply fast path: a short courtesy acknowledgment
  // (thank-you / confirmation / greeting) generated without any knowledge-base
  // lookup, so it has zero citations on purpose.
  trivial_reply?: boolean;
  // The canonical name of the intent the bypass route answered from
  // (`courtesy_<label>` for a classified courtesy note). Recorded for support, not
  // read by the sourcing mapping below.
  trivial_reply_intent?: string;
  // Set when the operator's "generate full answer" override ran the pipeline
  // and it gated out because the message carries no customer request. The
  // courtesy draft was kept; the UI shows a note instead of the override button.
  trivial_override_no_request?: boolean;
  // Snapshot of the FIRST generation's signals. The backend writes it on the
  // first manual regeneration so a regen can't turn a sourced answer into
  // NO_SOURCES in the operator's eyes.
  initial_signals?: ConfidenceSignals;
}

/** What the sources of a proposed answer let the UI do. SOURCED is the normal
 *  workflow; NO_SOURCES disables it ("we could not answer"); SEARCH_BYPASS is a
 *  courtesy reply with no sources by design, opened straight on the answer. */
export type AnswerSourcing = "SOURCED" | "NO_SOURCES" | "SEARCH_BYPASS";

export function getAnswerSourcing(
  signals: ConfidenceSignals | null | undefined,
): AnswerSourcing {
  // Prefer the first generation's snapshot when present so manual regens
  // never change the operator-visible classification.
  const effSignals = signals?.initial_signals ?? signals;
  // Trivial courtesy acknowledgments (thank-you / confirmation / greeting) have
  // zero citations by design. Checked first, before the zero-citation rule, so a
  // deliberate sourceless reply is shown as a normal answer rather than the
  // NO_SOURCES "we could not answer" state.
  // Only checks the live top-level signals, never the initial_signals snapshot:
  // a manual override regeneration can replace a trivial fast-path answer with
  // a real sourced one, and a stale trivial flag from the first pass must not
  // keep pinning the message to SEARCH_BYPASS.
  if (signals?.trivial_reply === true || signals?.reason === "trivial_reply")
    return "SEARCH_BYPASS";
  // Ontology-grounded answers (e.g. "please provide serial number") have zero
  // document citations by design — the answer is built from procedure /
  // customer_input ontology nodes, not retrieved chunks. Do NOT classify as
  // NO_SOURCES: that would disable the workflow CTA on a valid answer.
  if (effSignals?.reason === "ontology_grounded") return "SOURCED";
  const cited = effSignals?.cited_reference_count ?? 1; // default to 1 if not present (legacy)
  if (cited === 0 || effSignals?.llm_hedged) return "NO_SOURCES";
  return "SOURCED";
}

interface LowConfidenceBannerProps {
  score: number | null | undefined;
  hint?: string | null;
  signals?: ConfidenceSignals | null;
}

type TFunction = (key: string) => string;

function defaultMessage(
  signals: ConfidenceSignals | null | undefined,
  t: TFunction,
): string {
  if (signals?.had_unprocessed_images) {
    return t("lowConfidence.imageUnreadable");
  }
  if (signals?.reason === "ontology_grounded") {
    return t("lowConfidence.experienceBased");
  }
  return t("lowConfidence.noSources");
}

function shouldShow(
  _score: number | null | undefined,
  hint: string | null | undefined,
  signals: ConfidenceSignals | null | undefined,
): boolean {
  if (hint) return true;
  // llm_hedged deliberately does NOT trigger the banner: answers composed from
  // to-do facts legitimately relay phrasings like "nicht in der Dokumentation
  // enthalten", which the backend hedge detector still counts as an inability
  // admission. Suppressed until the confidence scorer is recalibrated for
  // fact-composed answers.
  if (signals?.had_unprocessed_images) return true;
  if (signals?.external_system_hint) return true;
  return false;
}

export function LowConfidenceBanner({
  score,
  hint,
  signals,
}: LowConfidenceBannerProps) {
  const { t } = useTranslation();
  if (!shouldShow(score, hint, signals)) return null;

  const message = hint ?? defaultMessage(signals, t);

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
      <p className="text-xs">{message}</p>
    </div>
  );
}
